export type DuplexSession = {
  sessionId: string;
  websocketUrl: string;
  token: string;
  sampleRate: number;
  encoding: string;
};

export type DuplexEvent = {
  type: string;
  role?: "user" | "assistant";
  text?: string;
  message?: string;
  code?: string;
  reason?: string;
  turn_id?: number;
  audio?: string;
  sample_rate?: number;
};

const RECOVERABLE_ERRORS = new Set(["provider_error", "invalid_message"]);

const captureModule = `class DuplexCapture extends AudioWorkletProcessor {
  constructor() { super(); this.frame = new Int16Array(320); this.offset = 0; }
  process(inputs) {
    const samples = inputs[0]?.[0];
    if (!samples) return true;
    for (const sample of samples) {
      this.frame[this.offset++] = Math.max(-1, Math.min(1, sample)) * 32767;
      if (this.offset === 320) {
        this.port.postMessage(this.frame.buffer, [this.frame.buffer]);
        this.frame = new Int16Array(320); this.offset = 0;
      }
    }
    return true;
  }
}
registerProcessor("duplex-capture", DuplexCapture);`;

export class DuplexClient {
  private socket: WebSocket | null = null;
  private context: AudioContext | null = null;
  private microphone: MediaStream | null = null;
  private capture: AudioWorkletNode | null = null;
  private input: MediaStreamAudioSourceNode | null = null;
  private sources = new Set<AudioBufferSourceNode>();
  private nextPlayback = 0;
  private stoppedTurn = -1;
  private playbackTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private ready = false;
  private ending = false;
  private rejectConnection: ((error: Error) => void) | null = null;

  constructor(private callbacks: {
    onEvent?: (event: DuplexEvent) => void;
    onClose?: () => void;
    onError?: (error: Error) => void;
    onPlayback?: (playing: boolean) => void;
  } = {}) {}

  async prepare() {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      throw new Error("Microphone access requires HTTPS or localhost.");
    }
    if (this.closed) throw new Error("Voice session is closed.");
    const context = new AudioContext({ sampleRate: 16000 });
    this.context = context;
    await context.resume();
    if (context.sampleRate !== 16000) throw new Error("This browser cannot capture at 16 kHz.");
    const microphone = await navigator.mediaDevices.getUserMedia({ audio: {
      channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true,
    } });
    if (this.closed) {
      microphone.getTracks().forEach((track) => track.stop());
      throw new Error("Voice session is closed.");
    }
    this.microphone = microphone;
    const url = URL.createObjectURL(new Blob([captureModule], { type: "text/javascript" }));
    try { await context.audioWorklet.addModule(url); } finally { URL.revokeObjectURL(url); }
    if (this.closed) throw new Error("Voice session is closed.");
    this.input = context.createMediaStreamSource(microphone);
    this.capture = new AudioWorkletNode(context, "duplex-capture");
    this.capture.port.onmessage = ({ data }: MessageEvent<ArrayBuffer>) => {
      if (!this.ready || this.ending || this.socket?.readyState !== WebSocket.OPEN) return;
      if (this.socket.bufferedAmount > 64000) {
        this.fail(new Error("The voice connection is too slow. Please reconnect."));
        return;
      }
      this.socket.send(data);
    };
    this.input.connect(this.capture);
    this.capture.connect(context.destination);
  }

  async connect(session: DuplexSession): Promise<void> {
    if (!this.context || this.closed) throw new Error("Prepare the microphone before connecting.");
    if (session.sampleRate !== 16000 || session.encoding !== "pcm_s16le") {
      throw new Error("Unsupported duplex audio format.");
    }
    return new Promise((resolve, reject) => {
      this.rejectConnection = reject;
      const socket = new WebSocket(session.websocketUrl);
      this.socket = socket;
      this.connectTimer = setTimeout(() => this.fail(new Error("Voice connection timed out.")), 15000);
      socket.onopen = () => this.send({ type: "auth", token: session.token });
      socket.onmessage = ({ data }: MessageEvent<string>) => {
        if (this.closed) return;
        try {
          const message = JSON.parse(data) as DuplexEvent;
          if (message.type === "ready") {
            this.ready = true;
            this.rejectConnection = null;
            if (this.connectTimer) clearTimeout(this.connectTimer);
            resolve();
          } else if (message.type === "audio") {
            this.play(message);
          } else if (message.type === "call_end") {
            this.ending = true;
            this.setMuted(true);
          } else if (message.type === "interrupted") {
            this.stoppedTurn = Math.max(this.stoppedTurn, message.turn_id ?? -1);
            this.stopPlayback();
          } else if (message.type === "error" && !RECOVERABLE_ERRORS.has(message.code ?? "")) {
            this.fail(new Error(message.message || "Voice provider request failed."));
            return;
          }
          this.callbacks.onEvent?.(message);
        } catch (error) {
          this.fail(error instanceof Error ? error : new Error("Invalid voice message."));
        }
      };
      socket.onerror = () => this.fail(new Error("Voice connection failed."));
      socket.onclose = () => {
        if (this.closed) return;
        const wasReady = this.ready;
        this.close();
        if (wasReady) this.callbacks.onClose?.();
        else this.callbacks.onError?.(new Error("Voice session was refused or expired."));
      };
    });
  }

  setMuted(muted: boolean) {
    this.microphone?.getAudioTracks().forEach((track) => { track.enabled = !muted; });
  }

  interrupt() { this.stopPlayback(); this.send({ type: "interrupt" }); }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.ready = false;
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.rejectConnection?.(new Error("Voice session ended before connecting."));
    this.rejectConnection = null;
    this.stopPlayback();
    this.capture?.disconnect();
    this.input?.disconnect();
    this.microphone?.getTracks().forEach((track) => track.stop());
    this.microphone = null;
    if (this.socket) {
      this.socket.onclose = this.socket.onerror = this.socket.onmessage = this.socket.onopen = null;
      this.socket.close();
      this.socket = null;
    }
    void this.context?.close();
    this.context = null;
  }

  private fail(error: Error) {
    if (this.closed) return;
    const pending = this.rejectConnection;
    this.rejectConnection = null;
    pending?.(error);
    this.close();
    if (!pending) this.callbacks.onError?.(error);
  }

  private send(value: Record<string, unknown>) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(value));
  }

  private playback(playing: boolean) {
    this.send({ type: "playback", playing });
    this.callbacks.onPlayback?.(playing);
  }

  private stopPlayback() {
    if (this.playbackTimer) clearTimeout(this.playbackTimer);
    for (const node of this.sources) { try { node.stop(); } catch {} }
    this.sources.clear();
    this.nextPlayback = 0;
    this.playback(false);
  }

  private play(message: DuplexEvent) {
    if (!this.context || !message.audio || (message.turn_id ?? -1) <= this.stoppedTurn) return;
    const bytes = Uint8Array.from(atob(message.audio), (value) => value.charCodeAt(0));
    const view = new DataView(bytes.buffer);
    const buffer = this.context.createBuffer(1, bytes.length / 2, message.sample_rate ?? 24000);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i++) samples[i] = view.getInt16(i * 2, true) / 32768;
    const node = this.context.createBufferSource();
    node.buffer = buffer;
    node.connect(this.context.destination);
    this.sources.add(node);
    node.onended = () => this.sources.delete(node);
    this.nextPlayback = Math.max(this.context.currentTime + 0.025, this.nextPlayback);
    node.start(this.nextPlayback);
    this.nextPlayback += buffer.duration;
    this.playback(true);
    if (this.playbackTimer) clearTimeout(this.playbackTimer);
    this.playbackTimer = setTimeout(() => this.playback(false),
      Math.max(0, this.nextPlayback - this.context.currentTime) * 1000 + 100);
  }
}
